/* eslint-disable max-lines, max-lines-per-function */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import './projectDetails.scss';
import ExpandableSections from './expandableSections';
import { ProgressEditModal, StatusEditModal, TimelineEditModal } from './projectDetailsModals';
import { toFiniteNumberOrNull } from './projectDetailsViewmodel';
import { MODULE_NAMES } from '../../../Constants/modules';
import { useAuth } from '../../../Hooks/useAuth';
import useProjectDetailsData from './useProjectDetailsData';
import useExpandableSections from './useExpandableSections';
import useHeaderModals from './useHeaderModals';
import ProjectHeaderSection from './components/projectHeaderSection';
import TimelineCard from './components/timelineCard';
import LeftMainCard from './components/leftMainCard';
import ManagersArchitectsCard from './components/managersArchitectsCard';
import AuditCard from './components/auditCard';
import CsatCard from './components/csatCard';
import RevenueCard from './components/revenueCard';
import UtilizationCard from './components/utilizationCard';
import GmCard from './components/gmCard';
import backIcon from '../../../Assets/images/svg/black_back_button.svg';
import loadingPuzzleGif from '../../../Assets/images/svg/loading puzzle.gif';

/**
 * ProjectDetails Component
 * Main container for project overview and expandable sections
 */

const PROJECT_ID_QUERY_KEY = 'projectId';
const PROJECT_ID_QUERY_KEY_FALLBACKS = ['id', 'projectSeqId', 'project_seq_id'];
const INDIAN_CURRENCY_FORMATTER = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrencyOrDash = (amount) => {
  const num = toFiniteNumberOrNull(amount);
  if (num === null) return '-';
  return INDIAN_CURRENCY_FORMATTER.format(num);
};

const hasModulePermission = (permissions, moduleName) => (
  Boolean(moduleName && permissions?.[moduleName] !== undefined)
);
const hasPermissionAction = (permissions, moduleName, action) => (
  Array.isArray(permissions?.[moduleName]) && permissions[moduleName].includes(action)
);
const isSuperAdminUser = (userProfile) => {
  if (!userProfile || Array.isArray(userProfile)) return false;

  const superAdminFlag = userProfile?.superAdmin ?? userProfile?.super_admin;
  if (Number(superAdminFlag) === 1 || superAdminFlag === true) {
    return true;
  }

  const roleDetails = Array.isArray(userProfile?.roleDetails)
    ? userProfile.roleDetails
    : userProfile?.roleDetails
      ? [userProfile.roleDetails]
      : [];

  return roleDetails.some((role) => {
    const roleName = String(role?.roleName || role?.name || '')
      .trim()
      .toLowerCase();
    const roleId = Number(role?.role_id ?? role?.roleId);
    return roleName === 'super admin' || roleId === 1;
  });
};

const getGmMetricCode = (label) => String(label || '').split(' ')[0].toUpperCase();

const isBlankValue = (value) => value === null || value === undefined || String(value).trim() === '';
const getProjectSeqId = (projectData) => projectData?.project_seq_id ?? projectData?.project_id;
const getProjectId = (projectData) => projectData?.project_id ?? projectData?.project_seq_id;
const normalizeProjectTypeVariantSource = (value) => {
  const normalized = String(value || '').trim()
    .toUpperCase()
    .replaceAll(/[^A-Z0-9]/g, '');

  if (['TM', 'TIMEANDMATERIAL', 'TIMEMATERIAL'].includes(normalized)) return 'tm';
  if (['FP', 'FPP', 'FIXEDPRICE'].includes(normalized)) return 'fp';
  return '';
};

const getProjectTypeVariant = (projectData) => {
  const projectTypeId = Number(projectData?.project_type_id ?? projectData?.projectTypeId);
  if (projectTypeId === 1) return 'fp';
  if (projectTypeId === 2) return 'tm';

  const typeFromBasicDetails = Array.isArray(projectData?.projectHeader?.basicDetails)
    ? projectData.projectHeader.basicDetails.find((item) => String(item?.label || '').trim()
      .toLowerCase() === 'type')?.value
    : '';
  const projectTypeVariant = normalizeProjectTypeVariantSource(
    projectData?.project_type
    || projectData?.projectType
    || typeFromBasicDetails
    || '',
  );
  if (projectTypeVariant) return projectTypeVariant;

  return normalizeProjectTypeVariantSource(projectData?.effort_type);
};

const resolveIconSrc = (icon) => {
  if (typeof icon === 'string') return icon;
  if (icon && typeof icon === 'object' && typeof icon.default === 'string') return icon.default;
  return '';
};

const handleIconError = (event) => {
  event.currentTarget.style.display = 'none';
};
const getErrorMessage = (error) => (
  typeof error === 'string' && error.trim() ? error : 'Unable to load project details'
);

const getCsatGaugeData = (csatSummary) => {
  if (!csatSummary) return null;
  const score = toFiniteNumberOrNull(csatSummary.score);
  const maxScore = toFiniteNumberOrNull(csatSummary.maxScore);
  if (score === null || maxScore === null || maxScore <= 0) return null;

  const percentage = (score / maxScore) * 100;
  const arcLength = 220;
  const filledLength = (percentage / 100) * arcLength;
  return {
    percentage,
    strokeDashoffset: arcLength - filledLength,
    displayScore: score.toFixed(2),
  };
};

const getRevenueChartData = (revenueData) => {
  if (!revenueData) return null;
  const invoiceAmount = toFiniteNumberOrNull(revenueData.invoiceAmount);
  const totalRevenue = toFiniteNumberOrNull(revenueData.totalRevenue);
  if (invoiceAmount === null || totalRevenue === null || totalRevenue <= 0) return null;

  const percentage = (invoiceAmount / totalRevenue) * 100;
  const circumference = 2 * Math.PI * 38.35;
  const filledLength = (percentage / 100) * circumference;
  return {
    percentage: Math.round(percentage),
    strokeDasharray: `${filledLength} ${circumference}`,
    displayPercentage: `${Math.round(percentage)}%`,
  };
};

const getMilestoneData = (revenueData) => {
  if (!revenueData) return null;

  const milestonesCompleted = toFiniteNumberOrNull(revenueData.milestonesCompleted);
  const totalMilestones = toFiniteNumberOrNull(revenueData.totalMilestones);
  if (milestonesCompleted === null || totalMilestones === null || totalMilestones <= 0) return null;

  const percentage = Math.round((milestonesCompleted / totalMilestones) * 100);
  return {
    completed: milestonesCompleted,
    total: totalMilestones,
    percentage,
    display: `${milestonesCompleted}/${totalMilestones} (${percentage}%)`,
  };
};

const renderPageState = (className, message, options = {}) => {
  const { imageSrc = '', imageAlt = '' } = options;

  return (
    <div className="project-details-wrapper">
      <div className={className}>
        {imageSrc ? <img src={imageSrc} alt={imageAlt} className="page-state-image" /> : null}
        <div className="page-state-message">{message}</div>
      </div>
    </div>
  );
};

const renderProjectDetailsContent = (viewModel) => {
  const {
    projectData,
    projectTypeVariant,
    renderProgressIcon,
    srManagerDisplay,
    srManagerDetails,
    managerRows,
    srArchitects,
    architects,
    deliveryManagerRows,
    auditPmoScore,
    auditQualityScore,
    auditTechScore,
    hasCsatSummary,
    csatGaugeData,
    hasRevenueData,
    maskedRevenueData,
    revenueChartData,
    milestoneData,
    hasUtilizationData,
    maskedUtilizationData,
    canViewCardData,
    hasChangeRequestAccess,
    hasGmData,
    visibleGmData,
    gmSeries,
    expandedSections,
    toggleSection,
    projectId,
    csatCustomers,
    csatCustomersLoading,
    csatCustomersError,
    lazyLoadCsat,
    progressLoading,
    effortsLoading,
    projectUnitsLoading,
    auditSectionLoading,
    riskSectionLoading,
    lazyLoadRisk,
    resourceSectionLoading,
    tbhSectionLoading,
    projectSeqId,
    refetchProjectData,
    headerModals,
    closeHeaderModal,
    submitTimelineEdit,
    handleTimelineHistory,
    submitting,
    statusOptions,
    submitStatusEdit,
    handleStatusHistory,
    submitProgressSummaryEdit,
    onEditProject,
    onEditStatus,
    onEditTimeline,
    onEditProgress,
    canViewEffortLoggedResources,
    canViewAllocationResources,
    canViewProgress,
    canViewAudit,
    hasAuditData,
    canViewCsat,
    canViewMilestoneInvoiceAmount,
    canViewGm,
    onBackToOverview,
    isSidebarCollapsed,
    fallbackDate,
  } = viewModel;
  const projectHeader = projectData?.projectHeader ?? {};
  const auditSummary = projectData?.auditSummary ?? {};
  const rightColumnClass = [
    'right-column',
    !canViewMilestoneInvoiceAmount ? 'no-revenue' : '',
    !canViewGm ? 'no-gm' : '',
    !(canViewCsat && hasCsatSummary) ? 'no-csat' : '',
    !canViewCardData ? 'no-utilization' : '',
  ].filter(Boolean).join(' ');

  const hasAuditCard = canViewAudit && hasAuditData;
  const leftSectionClass = `left-section ${!hasAuditCard ? 'no-audit' : ''}`;

  return (
    <div className={`project-details-wrapper ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="page-title">
        <div className="title-with-back">
          <button
            type="button"
            className="back-button"
            onClick={onBackToOverview}
            aria-label="Back to Project Overview"
          >
            <img src={backIcon} alt="back" />
          </button>
          <h1>Project details</h1>
        </div>
      </div>

      <div className="project-details">
        <ProjectHeaderSection
          title={projectHeader.title}
          status={projectHeader.status}
          statusTextColor={projectHeader.statusTextColor}
          accentColor={projectHeader.ragStatusColor}
          statusBgColor={projectHeader.statusBgColor}
          client={projectHeader.client}
          onEditStatus={onEditStatus}
          onEdit={onEditProject}
        />

        <div className="timeline-grid-wrapper">
          <TimelineCard
            timeline={projectHeader.timeline}
            onEdit={onEditTimeline}
          />

          <div className="main-grid">
            <div className={leftSectionClass}>
              <div className="top-row">
                <div className="left-column">
                  <div>
                    <LeftMainCard
                      basicDetails={projectHeader.basicDetails}
                      resources={projectHeader.resources}
                      progress={projectHeader.progress}
                      projectStatus={projectHeader.status}
                      projectType={projectTypeVariant}
                      showProgressAsNA={projectTypeVariant === 'tm'}
                      onEditProgress={onEditProgress}
                      canViewEffortLoggedResources={canViewEffortLoggedResources}
                      canViewAllocationResources={canViewAllocationResources}
                      canViewProgress={canViewProgress}
                      renderProgressIcon={renderProgressIcon}
                    />
                  </div>
                </div>

                <div className="middle-column">
                  <ManagersArchitectsCard
                    srManagerDisplay={srManagerDisplay}
                    srManagerDetails={srManagerDetails}
                    managerRows={managerRows}
                    srArchitects={srArchitects}
                    architects={architects}
                    deliveryManagerRows={deliveryManagerRows}
                  />
                </div>
              </div>

              {canViewAudit && hasAuditData && (
                <AuditCard
                  pmoScore={auditPmoScore}
                  qualityScore={auditQualityScore}
                  techScore={auditTechScore}
                  pmoDate={auditSummary?.pmo?.date}
                  qualityDate={auditSummary?.quality?.date}
                  techDate={auditSummary?.tech?.date}
                  canViewAudit={canViewAudit}
                />
              )}
            </div>

            <div className={rightColumnClass}>
              {canViewCsat && hasCsatSummary && (
                <CsatCard
                  hasCsatSummary={hasCsatSummary}
                  csatGaugeData={csatGaugeData}
                  csatSummary={projectData?.csatSummary}
                />
              )}

              {canViewMilestoneInvoiceAmount && (
                <RevenueCard
                  hasRevenueData={hasRevenueData}
                  revenueChartData={revenueChartData}
                  revenueData={maskedRevenueData}
                  milestoneData={milestoneData}
                  formatCurrencyOrDash={formatCurrencyOrDash}
                />
              )}

              {canViewCardData && (
                <UtilizationCard
                  hasUtilizationData={hasUtilizationData}
                  utilizationData={maskedUtilizationData}
                  projectTypeVariant={projectTypeVariant}
                  hasChangeRequestAccess={hasChangeRequestAccess}
                />
              )}

              {canViewGm && (
                <GmCard
                  hasGmData={hasGmData}
                  gmData={visibleGmData}
                  gmSeries={gmSeries}
                  fallbackDate={fallbackDate}
                />
              )}
            </div>
          </div>
        </div>

        <ExpandableSections
          expandedSections={expandedSections}
          onToggleSection={toggleSection}
          projectName={projectData?.projectHeader?.title || projectData?.project_name || ''}
          projectVertical={projectData?.project_vertical || ''}
          projectTypeVariant={projectTypeVariant}
          csatData={projectData.csatData}
          csatTableData={projectData.csatTableData}
          csatCustomers={csatCustomers}
          csatCustomersLoading={csatCustomersLoading}
          csatCustomersError={csatCustomersError}
          lazyLoadCsat={lazyLoadCsat}
          progressData={projectData.progressData}
          progressLoading={progressLoading}
          effortsData={projectData.effortsData}
          effortsLoading={effortsLoading}
          projectUnitsData={projectData.projectUnitsData}
          projectUnitsLoading={projectUnitsLoading}
          auditScoreData={projectData.auditScoreData}
          auditLoading={auditSectionLoading}
          riskRegisterData={projectData.riskRegisterData}
          riskLoading={riskSectionLoading}
          lazyLoadRisk={lazyLoadRisk}
          milestoneData={projectData.milestoneData}
          resourceCountData={projectData.resourceCountData}
          resourceAllocationData={projectData.resourceAllocationData}
          resourceLoading={resourceSectionLoading}
          tbhResources={projectData.tbhResources}
          tbhLoading={tbhSectionLoading}
          projectSeqId={projectSeqId}
          projectId={projectId}
          resourceTypeOptions={projectData.resourceTypeOptions}
          onRefreshData={refetchProjectData}
          projectStatus={projectData?.projectHeader?.status}
          projectData={projectData}
        />

        <TimelineEditModal
          isOpen={headerModals.timelineEdit.isOpen}
          data={headerModals.timelineEdit.data}
          onClose={() => closeHeaderModal('timelineEdit')}
          onSubmit={submitTimelineEdit}
          onHistory={handleTimelineHistory}
          isSubmitting={submitting.timelineEdit}
        />

        <StatusEditModal
          isOpen={headerModals.statusEdit.isOpen}
          data={headerModals.statusEdit.data}
          projectName={projectData?.project_name || projectData?.projectName || ''}
          statusOptions={statusOptions}
          onClose={() => closeHeaderModal('statusEdit')}
          onSubmit={submitStatusEdit}
          onHistory={handleStatusHistory}
          isSubmitting={submitting.statusEdit}
          isLoading={Boolean(headerModals.statusEdit.data?.isLoading)}
          isBlocked={Boolean(headerModals.statusEdit.data?.isBlocked)}
          blockedMessage={headerModals.statusEdit.data?.blockedMessage || ''}
        />

        <ProgressEditModal
          isOpen={headerModals.progressSummaryEdit.isOpen}
          data={headerModals.progressSummaryEdit.data}
          onClose={() => closeHeaderModal('progressSummaryEdit')}
          onSubmit={submitProgressSummaryEdit}
          title="Progress percentage"
          isSubmitting={submitting.progressSummaryEdit}
          headerAction={(
            <button
              type="button"
              className="modal-header-link"
              onClick={() => {
                closeHeaderModal('progressSummaryEdit');
                if (!expandedSections.progress) {
                  toggleSection('progress');
                }
                window.setTimeout(() => {
                  const target = document.getElementById('project-details-section-progress');
                  target?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
                }, 0);
              }}
            >
            History
            </button>
          )}
        />
      </div>
    </div>
  );
};

const ProjectDetails = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const permissions = userProfile?.permissions || {};
  const isSuperAdmin = isSuperAdminUser(userProfile);
  const canViewSgm = hasModulePermission(permissions, MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_SGM);
  const canViewAgm = hasModulePermission(permissions, MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_AGM);
  const canViewEgm = hasModulePermission(permissions, MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_EGM);
  const canViewGm = canViewSgm || canViewAgm || canViewEgm;
  const canViewEffortLoggedResources = hasPermissionAction(
    permissions,
    MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_lAST_MONTH,
    'read',
  );
  const canViewAllocationResources = hasPermissionAction(
    permissions,
    MODULE_NAMES.DELIVERY_ALLOCATION_DETAILS_VIEW,
    'read',
  );
  const canEditDetailProjectFields = hasPermissionAction(
    permissions,
    MODULE_NAMES.DELIVERY_PROJECT_DETAILS_EDIT,
    'update',
  );
  const canEditManagerBasicProjectFields = hasPermissionAction(
    permissions,
    MODULE_NAMES.DELIVERY_PROJECT_MANAGER_DETAILS_EDIT,
    'update',
  );
  const canEditProjectDetails = isSuperAdmin || (
    canEditDetailProjectFields || canEditManagerBasicProjectFields
  );
  const canEditTimelineInDetails = isSuperAdmin || hasPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_TIMELINE_CHANGE_REQUEST,
    'update',
  ) || hasPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_TIMELINE_CHANGE_REQUEST,
    'create',
  );
  const canEditProjectProgress = hasPermissionAction(
    permissions,
    MODULE_NAMES.DELIVERY_PROJECT_PROGRESS,
    'update',
  );
  const canViewProgress = hasPermissionAction(
    permissions,
    MODULE_NAMES.DELIVERY_PROJECT_PROGRESS,
    'read',
  );
  const canViewAudit = hasPermissionAction(
    permissions,
    MODULE_NAMES.DELIVERY_AUDIT_SCORE,
    'read',
  );
  const canViewCsat = hasPermissionAction(
    permissions,
    MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_CSAT,
    'read',
  );
  const canViewMilestoneInvoiceAmount = hasPermissionAction(
    permissions,
    MODULE_NAMES.FINANCE_MILESTONE_INVOICE_AMOUNT_VIEW,
    'read',
  );
  const canViewEstimationVsActualMis = isSuperAdmin || hasPermissionAction(
    permissions,
    MODULE_NAMES.MIS_ESTIMATED_VS_ACTUAL_REPORT,
    'read',
  );
  const canViewProjectBillability = isSuperAdmin || hasPermissionAction(
    permissions,
    MODULE_NAMES.DELIVERY_PROJECT_DETAILS_UTILIZATION,
    'read',
  );
  const hasChangeRequestPermission = isSuperAdmin || hasPermissionAction(
    permissions,
    MODULE_NAMES.PROJECT_CHANGE_REQUEST,
    'read',
  );
  const canViewProjectStatusHistory = isSuperAdmin || hasPermissionAction(
    permissions,
    MODULE_NAMES.DELIVERY_PROJECT_STATUS,
    'read',
  );

  const {
    projectData,
    loading,
    error,
    refetchProjectData,
    csatCustomers,
    csatCustomersLoading,
    csatCustomersError,
    lazyLoadCsat,
    progressLoading,
    lazyLoadProgress,
    effortsLoading,
    lazyLoadEfforts,
    projectUnitsLoading,
    lazyLoadProjectUnits,
    auditSectionLoading,
    lazyLoadAudit,
    riskSectionLoading,
    lazyLoadRisk,
    resourceSectionLoading,
    lazyLoadResources,
    tbhSectionLoading,
    lazyLoadTbhResources,
  } = useProjectDetailsData({
    queryKey: PROJECT_ID_QUERY_KEY,
    fallbackKeys: PROJECT_ID_QUERY_KEY_FALLBACKS,
  });

  const projectSeqId = getProjectSeqId(projectData);
  const projectId = getProjectId(projectData);
  const projectTypeVariant = getProjectTypeVariant(projectData);
  const fallbackDate = useMemo(() => {
    const projectHeader = projectData?.projectHeader ?? {};
    const resources = projectHeader?.resources || [];
    const progress = projectHeader?.progress || [];
    const allItems = [...resources, ...progress];
    const dates = allItems
      .map((item) => {
        const text = String(item?.label || '');
        const onTokenIndex = text.toLowerCase().indexOf('(on ');
        if (onTokenIndex === -1) return null;
        const closeParenIndex = text.indexOf(')', onTokenIndex + 4);
        if (closeParenIndex === -1) return null;
        return text.slice(onTokenIndex + 4, closeParenIndex).trim();
      })
      .filter(Boolean);
    return dates[0] || null;
  }, [projectData?.projectHeader]);
  const handleBackToOverview = useCallback(() => {
    navigate('/delivery/project-overview');
  }, [navigate]);

  const handleHeaderEdit = useCallback(() => {
    if (isBlankValue(projectId)) return;
    const query = new URLSearchParams({
      projectId: String(projectId),
      mode: 'edit',
      isEditable: 'true',
    });
    if (!isBlankValue(projectSeqId)) {
      query.set('projectSeqId', String(projectSeqId));
    }
    const variant = getProjectTypeVariant(projectData);
    if (variant) {
      query.set('variant', variant);
    }
    navigate(`/delivery/add-projects?${query.toString()}`);
  }, [navigate, projectData, projectId, projectSeqId]);

  const { expandedSections, toggleSection } = useExpandableSections();

  useEffect(() => {
    if (projectId) {
      lazyLoadCsat(projectId);
    }
  }, [projectId, lazyLoadCsat]);

  useEffect(() => {
    if (expandedSections.csat && projectId) {
      lazyLoadCsat(projectId);
    }
  }, [expandedSections.csat, projectId, lazyLoadCsat]);

  useEffect(() => {
    if (expandedSections.progress && projectId) {
      lazyLoadProgress(projectId);
    }
  }, [expandedSections.progress, projectId, lazyLoadProgress]);

  useEffect(() => {
    if (expandedSections.efforts && projectId) {
      lazyLoadEfforts(projectId);
    }
  }, [expandedSections.efforts, projectId, lazyLoadEfforts]);

  useEffect(() => {
    if (expandedSections.projectUnits && projectId) {
      lazyLoadProjectUnits(projectId);
    }
  }, [expandedSections.projectUnits, projectId, lazyLoadProjectUnits]);

  // Lazy-load deferred section data when expanded
  useEffect(() => {
    if (expandedSections.audit) {
      lazyLoadAudit();
    }
  }, [expandedSections.audit, lazyLoadAudit]);

  useEffect(() => {
    if (expandedSections.risk) {
      lazyLoadRisk();
    }
  }, [expandedSections.risk, lazyLoadRisk]);

  useEffect(() => {
    if (expandedSections.resourceCount || expandedSections.resourceDetails || expandedSections.allocationDetails) {
      lazyLoadResources();
    }
  }, [
    expandedSections.resourceCount,
    expandedSections.resourceDetails,
    expandedSections.allocationDetails,
    lazyLoadResources,
  ]);

  useEffect(() => {
    if (expandedSections.tbhResources) {
      lazyLoadTbhResources();
    }
  }, [expandedSections.tbhResources, lazyLoadTbhResources]);
  const {
    headerModals,
    statusOptions,
    closeHeaderModal,
    openStatusEdit,
    openTimelineEdit,
    openProgressSummaryEdit,
    submitStatusEdit,
    submitTimelineEdit,
    submitProgressSummaryEdit,
    handleStatusHistory,
    handleTimelineHistory,
    submitting,
  } = useHeaderModals(projectData, {
    projectSeqId,
    onRefreshData: refetchProjectData,
    canEditTimeline: canEditTimelineInDetails,
  });
  const renderProgressIcon = (item) => {
    if (typeof item?.icon === 'string' && item.icon.includes('.svg')) {
      return <img src={resolveIconSrc(item.icon)} alt="" onError={handleIconError} />;
    }

    return item?.icon;
  };

  const srManagerDisplay = projectData?.peopleData?.srManagerDisplay ?? '-';
  const srManagerDetails = projectData?.peopleData?.srManagerDetails ?? null;
  const managerRows = projectData?.peopleData?.managerRows ?? [];
  const srArchitects = projectData?.peopleData?.srArchitects ?? [];
  const architects = projectData?.peopleData?.architects ?? [];
  const deliveryManagerRows = projectData?.peopleData?.deliveryManagerRows ?? [];

  const csatGaugeData = useMemo(() => {
    return getCsatGaugeData(projectData?.csatSummary);
  }, [projectData?.csatSummary]);

  const hasCsatSummary = Boolean(projectData?.csatSummary);
  const maskedRevenueData = canViewMilestoneInvoiceAmount
    ? projectData?.revenueData
    : {
      totalRevenue: 0,
      invoiceAmount: 0,
    };
  const hasRevenueData = canViewMilestoneInvoiceAmount ? Boolean(projectData?.revenueData) : true;
  const canViewCardData = projectTypeVariant === 'tm' ? canViewProjectBillability : canViewEstimationVsActualMis;
  const maskedUtilizationData = canViewCardData
    ? projectData?.utilizationData
    : {
      mode: projectTypeVariant || 'fp',
      initialEstEffort: 0,
      approvedCREffort: 0,
      approvedCRPercentage: 0,
      totalEstEffort: 0,
      currentActEffort: 0,
      estEffortDeviation: 0,
      overallUtilization: 0,
      latestUtilizationPercentage: 0,
      latestUtilizationDate: null,
    };
  const hasUtilizationData = canViewCardData ? Boolean(projectData?.utilizationData) : true;

  const visibleGmData = useMemo(() => {
    const gmRows = Array.isArray(projectData?.gmData) ? projectData.gmData : [];
    const gmRowMap = new Map(gmRows.map((item) => [getGmMetricCode(item?.label), item]));
    const gmColors = {
      SGM: '#75DDB6',
      AGM: '#FFD15C',
      EGM: '#FF8E93',
    };

    return ['SGM', 'AGM', 'EGM'].flatMap((metricCode) => {
      const canViewMetric = (
        (metricCode === 'SGM' && canViewSgm)
        || (metricCode === 'AGM' && canViewAgm)
        || (metricCode === 'EGM' && canViewEgm)
      );
      const sourceRow = gmRowMap.get(metricCode);

      if (!canViewMetric || !sourceRow) {
        return [];
      }

      return [{
        ...sourceRow,
        label: sourceRow?.label || metricCode,
        color: sourceRow?.color || gmColors[metricCode],
      }];
    });
  }, [projectData?.gmData, canViewSgm, canViewAgm, canViewEgm]);

  const hasGmData = visibleGmData.length > 0;
  const auditPmoScore = toFiniteNumberOrNull(projectData?.auditSummary?.pmo?.score);
  const auditQualityScore = toFiniteNumberOrNull(projectData?.auditSummary?.quality?.score);
  const auditTechScore = toFiniteNumberOrNull(projectData?.auditSummary?.tech?.score);
  const hasAuditData = auditPmoScore !== null || auditQualityScore !== null || auditTechScore !== null;

  const revenueChartData = useMemo(() => {
    return getRevenueChartData(maskedRevenueData);
  }, [maskedRevenueData]);

  const milestoneData = useMemo(() => {
    return canViewMilestoneInvoiceAmount
      ? getMilestoneData(projectData?.revenueData)
      : {
        completed: 0,
        total: 0,
        percentage: 0,
        display: '0/0 (0%)',
      };
  }, [canViewMilestoneInvoiceAmount, projectData?.revenueData]);

  const gmSeries = useMemo(() => {
    if (!visibleGmData.length) return [];
    return [{ data: visibleGmData.map((item) => item.value) }];
  }, [visibleGmData]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    const updateSidebarState = () => {
      setIsSidebarCollapsed(Boolean(document.querySelector('.sidebar.collapsed')));
    };

    updateSidebarState();

    const sidebarElement = document.querySelector('.sidebar');
    if (!sidebarElement || typeof MutationObserver === 'undefined') {
      window.addEventListener('resize', updateSidebarState);
      return () => window.removeEventListener('resize', updateSidebarState);
    }

    const observer = new MutationObserver(updateSidebarState);
    observer.observe(sidebarElement, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', updateSidebarState);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateSidebarState);
    };
  }, []);

  if (loading) {
    return renderPageState('loading-state', '', {
      imageSrc: loadingPuzzleGif,
      imageAlt: 'Loading project details',
    });
  }

  if (error) {
    return renderPageState('error-state', `Error loading project: ${getErrorMessage(error)}`);
  }

  if (!projectData) {
    return renderPageState('no-data-state', 'No project data available');
  }

  return renderProjectDetailsContent({
    projectData,
    projectTypeVariant,
    renderProgressIcon,
    srManagerDisplay,
    srManagerDetails,
    managerRows,
    srArchitects,
    architects,
    deliveryManagerRows,
    auditPmoScore,
    auditQualityScore,
    auditTechScore,
    hasCsatSummary,
    csatGaugeData,
    hasRevenueData,
    maskedRevenueData,
    revenueChartData,
    milestoneData,
    hasUtilizationData,
    maskedUtilizationData,
    canViewCardData,
    hasChangeRequestAccess: hasChangeRequestPermission,
    hasGmData,
    visibleGmData,
    gmSeries,
    expandedSections,
    toggleSection,
    projectId,
    csatCustomers,
    csatCustomersLoading,
    csatCustomersError,
    lazyLoadCsat,
    progressLoading,
    effortsLoading,
    projectUnitsLoading,
    auditSectionLoading,
    riskSectionLoading,
    lazyLoadRisk,
    resourceSectionLoading,
    tbhSectionLoading,
    projectSeqId,
    refetchProjectData,
    headerModals,
    closeHeaderModal,
    submitTimelineEdit,
    handleTimelineHistory,
    submitting,
    statusOptions,
    submitStatusEdit,
    handleStatusHistory,
    submitProgressSummaryEdit,
    onEditProject: canEditProjectDetails ? handleHeaderEdit : null,
    onEditStatus: canViewProjectStatusHistory ? openStatusEdit : null,
    onEditTimeline: canEditTimelineInDetails ? openTimelineEdit : null,
    onEditProgress: canEditProjectProgress ? openProgressSummaryEdit : null,
    canViewEffortLoggedResources,
    canViewAllocationResources,
    canViewProgress,
    canViewAudit,
    hasAuditData,
    canViewCsat,
    canViewMilestoneInvoiceAmount,
    canViewGm,
    onBackToOverview: handleBackToOverview,
    isSidebarCollapsed,
    fallbackDate,
    canViewEstimationVsActualMis,
  });
};

export default ProjectDetails;
